import { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/components/ui/use-toast';
import { BudgetGameState, BudgetItem, INITIAL_STATE, BUDGET_CATEGORIES } from './types';
import { supabase } from '@/lib/supabase';
import { addPoints } from '@/lib/points';
import { calculateBudgetPoints, GAME_POINTS } from '@/lib/cerebrix-config';
import useUser from '@/hooks/useUser';

export const useBudgetGame = () => {
  const [state, setState] = useState<BudgetGameState>(INITIAL_STATE);
  const { user } = useUser();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const isEndingGame = useRef(false);

  // Obtener el ID del juego
  const { data: gameId } = useQuery({
    queryKey: ['game-id', 'budget'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('games')
        .select('id')
        .eq('type', 'budget')
        .single();
      
      if (error) {
        console.error('Error fetching game ID:', error);
        return null;
      }
      
      return data?.id;
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 5 // 5 minutos
  });

  // Iniciar el juego
  const startGame = useCallback(() => {
    setState({
      ...INITIAL_STATE,
      gameStarted: true,
      timeLeft: 300, // 5 minutos
      stats: {
        ...INITIAL_STATE.stats,
        totalCategories: BUDGET_CATEGORIES.length,
        startTime: Date.now()
      }
    });
  }, []);

  // Agregar un ítem al presupuesto
  const addItem = useCallback((item: Omit<BudgetItem, 'id'>) => {
    setState(prev => {
      const newItem = { ...item, id: prev.items.length + 1 };
      const newItems = [...prev.items, newItem];
      
      const totalIncome = newItems
        .filter(i => i.type === 'income')
        .reduce((sum, i) => sum + i.amount, 0);
      
      const totalExpenses = newItems
        .filter(i => i.type === 'expense')
        .reduce((sum, i) => sum + i.amount, 0);

      const balance = totalIncome - totalExpenses;
      const hasSavings = newItems.some(i => i.category === 'Ahorro' && i.type === 'expense' && i.amount > 0);
      
      // Calcular puntuación en tiempo real
      const categoriesUsed = [...new Set(newItems.map(i => i.category))];
      const isBalanced = totalIncome >= totalExpenses;
      const categoryCoverage = categoriesUsed.length / BUDGET_CATEGORIES.length;
      
      // Calcular puntuación
      let score = 0;
      
      // Puntos por presupuesto balanceado
      if (isBalanced) {
        score += GAME_POINTS.BUDGET.BALANCE;
      }
      
      // Puntos por incluir ahorros
      if (hasSavings) {
        score += GAME_POINTS.BUDGET.SAVINGS;
      }
      
      // Puntos por usar categorías
      score += Math.floor(categoryCoverage * GAME_POINTS.BUDGET.CATEGORIES);
      
      // Bonus por usar todas las categorías
      if (categoriesUsed.length === BUDGET_CATEGORIES.length) {
        score += 1;
      }

      return {
        ...prev,
        items: newItems,
        totalIncome,
        totalExpenses,
        balance,
        score: Math.min(score, GAME_POINTS.BUDGET.MAX_POINTS),
        stats: {
          ...prev.stats,
          categoriesUsed,
          isBalanced,
          hasSavings,
          categoryCoverage
        }
      };
    });
  }, []);

  // Eliminar un ítem del presupuesto
  const removeItem = useCallback((itemId: number) => {
    setState(prev => {
      const newItems = prev.items.filter(item => item.id !== itemId);
      
      const totalIncome = newItems
        .filter(i => i.type === 'income')
        .reduce((sum, i) => sum + i.amount, 0);
      
      const totalExpenses = newItems
        .filter(i => i.type === 'expense')
        .reduce((sum, i) => sum + i.amount, 0);

      const balance = totalIncome - totalExpenses;
      const hasSavings = newItems.some(i => i.category === 'Ahorro' && i.type === 'expense' && i.amount > 0);
      
      // Calcular puntuación en tiempo real
      const categoriesUsed = [...new Set(newItems.map(i => i.category))];
      const isBalanced = totalIncome >= totalExpenses;
      const categoryCoverage = categoriesUsed.length / BUDGET_CATEGORIES.length;
      
      // Calcular puntuación
      let score = 0;
      
      // Puntos por presupuesto balanceado
      if (isBalanced) {
        score += GAME_POINTS.BUDGET.BALANCE;
      }
      
      // Puntos por incluir ahorros
      if (hasSavings) {
        score += GAME_POINTS.BUDGET.SAVINGS;
      }
      
      // Puntos por usar categorías
      score += Math.floor(categoryCoverage * GAME_POINTS.BUDGET.CATEGORIES);
      
      // Bonus por usar todas las categorías
      if (categoriesUsed.length === BUDGET_CATEGORIES.length) {
        score += 1;
      }

      return {
        ...prev,
        items: newItems,
        totalIncome,
        totalExpenses,
        balance,
        score: Math.min(score, GAME_POINTS.BUDGET.MAX_POINTS),
        stats: {
          ...prev.stats,
          categoriesUsed,
          isBalanced,
          hasSavings,
          categoryCoverage
        }
      };
    });
  }, []);

  // Finalizar el juego manualmente
  const endGame = useCallback(async () => {
    if (isEndingGame.current) return;
    isEndingGame.current = true;
    
    setState(prev => ({
      ...prev,
      isGameOver: true,
      timeLeft: 0
    }));
    
    try {
      await updateProgress.mutateAsync();
    } catch (error) {
      console.error('Error al finalizar el juego:', error);
    } finally {
      isEndingGame.current = false;
    }
  }, []);

  // Actualizar progreso y guardar en la base de datos
  const updateProgress = useMutation({
    mutationFn: async () => {
      if (!user?.id || !gameId) {
        throw new Error('No session or game ID found');
      }

      // Calcular tiempo usado
      const timeUsed = state.stats.startTime ? Math.floor((Date.now() - state.stats.startTime) / 1000) : 0;
      
      // Calcular Cerebrix ganados
      const cerebrixEarned = calculateBudgetPoints({
        isBalanced: state.stats.isBalanced,
        hasSavings: state.stats.hasSavings,
        categoriesUsed: state.stats.categoriesUsed.length,
        totalCategories: state.stats.totalCategories,
        errorCount: state.stats.errorCount
      });

      // Asegurar que la puntuación nunca sea null
      const finalScore = Math.max(state.score || 0, 0);

      // Agregar Cerebrix ganados
      const { transaction, newTotalPoints } = await addPoints({
        userId: user.id,
        amount: finalScore, // Usar finalScore en lugar de cerebrixEarned
        source: 'game',
        sourceId: gameId,
        description: `Completaste el juego de presupuesto familiar con ${
          state.stats.isBalanced ? 'un presupuesto balanceado' : 'oportunidades de mejora'
        }${state.stats.hasSavings ? ' e incluiste ahorros' : ''}`
      });

      // Actualizar el estado con los Cerebrix ganados
      setState(prev => ({
        ...prev,
        cerebrixEarned
      }));

      return {
        cerebrixEarned,
        newTotalPoints,
        stats: state.stats,
        timeUsed
      };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['user-points']);
      
      // No mostrar toast aquí, lo manejamos en el componente
    },
    onError: (error) => {
      console.error('Error updating progress:', error);
      toast({
        title: "Error al guardar progreso",
        description: "No se pudo guardar tu progreso. Por favor intenta de nuevo.",
        variant: "destructive"
      });
    }
  });

  // Timer del juego
  useEffect(() => {
    if (!state.gameStarted || state.isGameOver) return;

    const timer = setInterval(() => {
      setState(prev => {
        if (prev.timeLeft <= 1) {
          clearInterval(timer);
          return {
            ...prev,
            timeLeft: 0,
            isGameOver: true
          };
        }
        
        return {
          ...prev,
          timeLeft: prev.timeLeft - 1
        };
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [state.gameStarted, state.isGameOver]);

  // Finalizar el juego cuando el tiempo se acaba
  useEffect(() => {
    if (state.isGameOver && !isEndingGame.current && !updateProgress.isLoading) {
      updateProgress.mutate();
    }
  }, [state.isGameOver]);

  // Formatear el tiempo restante
  const formatTime = useCallback((seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);

  return {
    state,
    startGame,
    addItem,
    removeItem,
    endGame,
    updateProgress,
    formatTime,
    isUpdatingProgress: updateProgress.isLoading
  };
};
